import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Stack,
  Chip,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import heroImage from "../images/Carrusel1.webp";
import PaymentsIcon from "@mui/icons-material/Payments";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import { ubikaLead } from "../api/dashboard";

const Hero1 = () => {
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, type: "success", msg: "" });

  // valida email o número (muy simple, podés endurecerlo si querés)
  const isValid = useMemo(() => {
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRx = /^[\d\s()+-]{6,}$/;
    return emailRx.test(contact.trim()) || phoneRx.test(contact.trim());
  }, [contact]);

  const parseUtm = () => {
    try {
      const p = new URLSearchParams(window.location.search);
      const utm = {};
      [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
      ].forEach((k) => {
        if (p.get(k)) utm[k] = p.get(k);
      });
      return Object.keys(utm).length ? utm : undefined;
    } catch {
      return undefined;
    }
  };

  const handleSubmit = async () => {
    if (!isValid) {
      setSnack({
        open: true,
        type: "warning",
        msg: "Ingresá un email o WhatsApp válido.",
      });
      return;
    }

    setLoading(true);

    try {
      const body = {
        contact: contact.trim(),
        source: "hero1", // origen del lead
        page: typeof window !== "undefined" ? window.location.href : "",
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        ts: new Date().toISOString(),
        utm: parseUtm(), // captura UTM si hay
      };

      const res = await ubikaLead(body); // 👈 devuelve JSON
      console.log(res);
      if (res?.data.success) {
        setSnack({
          open: true,
          type: "success",
          msg: "¡Listo! Te contactamos a la brevedad.",
        });
        setContact("");
      } else {
        throw new Error(res?.data.message || "Error desconocido");
      }
    } catch (error) {
      console.error("Error enviando lead:", error);
      setSnack({
        open: true,
        type: "error",
        msg: "No pudimos enviar tu solicitud. Probá de nuevo en un momento.",
      });
    } finally {
      setLoading(false);
    }
  };

  const chipSx = {
    color: "#fff",
    borderColor: "rgba(255,255,255,0.28)",
    backgroundColor: "rgba(255,255,255,0.14)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: "999px",
    px: 1.1,
    py: 0.6,
    fontWeight: 600,
    "& .MuiChip-icon": {
      color: "inherit",
      opacity: 0.95,
    },
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.20)",
      borderColor: "rgba(255,255,255,0.40)",
    },
  };

  const isEmpty = contact.trim().length === 0;

  return (
    <Box
      sx={{
        position: "relative",
        // ✅ Gradiente invertida: violeta arriba → blanco abajo
        background:
          "linear-gradient(180deg, #903AF2 30%, #b88af6 70%, #ffffff 100%)",
        color: "#fff",
        px: { xs: 3, md: 14, xl: 28 },
        py: { xs: 6, md: 7 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textAlign: "center",
        overflow: "hidden",
      }}
    >
      {/* glow sutil para darle “premium” sin ensuciar */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(900px 400px at 20% 20%, rgba(255,255,255,0.18), rgba(255,255,255,0) 55%)",
        }}
      />

      <Grid
        container
        spacing={{ xs: 4, md: 6 }}
        alignItems="center"
        justifyContent="center"
        sx={{ position: "relative", zIndex: 1 }}
      >
        {/* Texto */}
        <Grid item xs={12} md={6}>
          <Box sx={{ maxWidth: 620, mx: { xs: "auto", md: 0 } }}>
            <Typography
              sx={{
                fontSize: { xs: "0.85rem", md: "0.95rem" },
                letterSpacing: 1.4,
                textTransform: "uppercase",
                mb: 1.2,
                textAlign: { xs: "center", md: "left" },
                opacity: 0.9,
                textShadow: "0 2px 10px rgba(0,0,0,0.25)",
              }}
            >
              Tecnología para potenciar tu hotel
            </Typography>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                fontFamily: "'Red Hat Display', sans-serif",
                mb: 2,
                fontSize: { xs: "2.1rem", md: "3rem" },
                textAlign: { xs: "center", md: "left" },
                lineHeight: 1.08,
                letterSpacing: -0.5,
                textShadow: "0 10px 24px rgba(0,0,0,0.28)",
              }}
            >
              Software para hoteles{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: 900,
                  background:
                    "linear-gradient(90deg, #eaf7cf 30%, #ffffff 80%, #ffffff 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "none",
                }}
              >
                all-in-one
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "1.05rem", md: "1.2rem" },
                mb: 2.6,
                textAlign: { xs: "center", md: "left" },
                maxWidth: 540,
                fontFamily: "'Merriweather Sans', sans-serif",
                opacity: 0.95,
                textShadow: "0 6px 18px rgba(0,0,0,0.16)",
              }}
            >
              Centralizá reservas, pagos y atención al cliente en una plataforma
              intuitiva y profesional.
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              justifyContent={{ xs: "center", md: "flex-start" }}
              sx={{ mb: 2.3 }}
            >
              <Chip
                icon={<CalendarMonthIcon />}
                label="Calendario unificado"
                variant="outlined"
                sx={chipSx}
              />
              <Chip
                icon={<PaymentsIcon />}
                label="Pagos online"
                variant="outlined"
                sx={chipSx}
              />
              <Chip
                icon={<HeadsetMicIcon />}
                label="Soporte en español"
                variant="outlined"
                sx={chipSx}
              />
            </Stack>

            {/* Captura rápida (WhatsApp o email) */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.4}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{
                mt: 0.5,
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <TextField
                placeholder="Tu WhatsApp o email"
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                error={Boolean(contact) && !isValid}
                sx={{
                  width: { xs: "100%", sm: 360 },
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 10px 22px rgba(0,0,0,0.14)",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
                    "&:hover fieldset": { borderColor: "#ee964b" },
                    "&.Mui-focused fieldset": {
                      borderColor: "#ee964b",
                      borderWidth: 2,
                    },
                  },
                  "& input": {
                    py: 1.35,
                  },
                }}
              />

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !isValid}
                sx={{
                  px: 3.2,
                  py: 1.25,
                  fontWeight: 900,
                  borderRadius: "12px",
                  textTransform: "none",
                  boxShadow: "0 14px 26px rgba(0,0,0,0.18)",
                  backgroundColor: "#ee964b",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#e3883b",
                  },
                  // ✅ cuando está disabled (vacío o inválido) NO queda gris:
                  "&.Mui-disabled": {
                    backgroundColor: isEmpty ? "#c46c24" : "#d77f3c",
                    color: "#fff",
                    opacity: 1,
                    boxShadow: "none",
                  },
                }}
              >
                {loading ? "Enviando..." : "Contactarme"}
              </Button>
            </Stack>

            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 1.1,
                opacity: 0.92,
                color: "#903AF2",
                textAlign: { xs: "center", md: "left" },
                textShadow: "0 6px 18px rgba(0,0,0,0.16)",
              }}
            >
              Sin tarjeta. Configuración inicial incluida.
            </Typography>

            {/* Social proof en texto */}
            <Stack
              direction="row"
              spacing={1.2}
              mt={3.2}
              flexWrap="wrap"
              justifyContent={{ xs: "center", md: "flex-start" }}
              sx={{
                // ✅ abajo ahora es blanco → esto tiene que ser violeta
                color: "#903AF2",
                opacity: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Integra:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                Booking
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                •
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                Airbnb
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                •
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                Hostelworld
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                •
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                Web propia
              </Typography>
            </Stack>
          </Box>
        </Grid>

        {/* Imagen */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
            }}
          >
            <Box
              component="img"
              src={heroImage}
              alt="Vista previa del software"
              sx={{
                width: "100%",
                maxWidth: { xs: 520, md: 560 },
                display: "block",
                borderRadius: "18px",
                backgroundColor: "rgba(255,255,255,0.18)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.25)",
                boxShadow: "0 22px 44px rgba(0,0,0,0.22)",
              }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Snackbar feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Hero1;